import { StatusChipVariant } from '@/components/ui/StatusChip';

/**
 * Status Chip Utility - Maps status values to StatusChip variants
 *
 * This utility provides consistent mapping from semantic status values
 * to StatusChip visual variants across all entity types in the application.
 *
 * Variant Mapping:
 * - info (Blue): Positive/active states (Active, Approved, Remediated, Completed)
 * - high (Orange): Pending/warning states (Triaged, Pending, Inactive)
 * - critical (Red): Negative/error states (Opened, Expiring, Rejected, Failed)
 * - neutral (Green): Neutral/accepted states (Frozen, Accepted)
 * - muted (Gray): Inactive/unknown states (Deleted, Unknown, Queued, No scans)
 */

/**
 * Maps border color class names to StatusChip variants.
 * This is useful for migrating from StatusBadge (border-based) to StatusChip (variant-based).
 */
export function mapBorderClassToVariant(className: string): StatusChipVariant {
  if (className.includes('border-chariot-stroke-info')) {
    return 'info';
  }
  if (className.includes('border-chariot-stroke-high')) {
    return 'high';
  }
  if (className.includes('border-chariot-stroke-critical')) {
    return 'critical';
  }
  if (className.includes('border-chariot-text-primary')) {
    return 'neutral';
  }
  if (className.includes('border-chariot-text-secondary')) {
    return 'muted';
  }
  // ScanStatus uses non-chariot colors
  if (className.includes('border-green-500')) {
    return 'info';
  }
  if (className.includes('border-blue-500')) {
    return 'info';
  }
  if (className.includes('border-yellow-500')) {
    return 'muted';
  }
  if (className.includes('border-red-500')) {
    return 'critical';
  }
  if (className.includes('border-gray-400')) {
    return 'muted';
  }
  return 'default';
}

/**
 * Asset Status Variants
 */
export const assetStatusVariants: Record<string, StatusChipVariant> = {
  active: 'info',
  inactive: 'high',
  expiring: 'critical',
  frozen: 'neutral',
  deleted: 'muted',
  pending: 'muted',
};

/**
 * Seed Status Variants
 */
export const seedStatusVariants: Record<string, StatusChipVariant> = {
  approved: 'info',
  'active passive': 'info',
  pending: 'high',
  rejected: 'critical',
  frozen: 'neutral',
  deleted: 'muted',
};

/**
 * Vulnerability Status Variants
 */
export const vulnerabilityStatusVariants: Record<string, StatusChipVariant> = {
  triaged: 'high',
  opened: 'critical',
  remediated: 'info',
  accepted: 'neutral',
  deleted: 'muted',
};

/**
 * Job/Scan Status Variants
 */
export const jobStatusVariants: Record<string, StatusChipVariant> = {
  completed: 'info',
  pass: 'info',
  failed: 'critical',
  fail: 'critical',
  running: 'info',
  queued: 'muted',
  'no scans': 'muted',
};

/**
 * Agent Status Variants
 */
export const agentStatusVariants: Record<string, StatusChipVariant> = {
  online: 'info',
  offline: 'critical',
};

/**
 * Get variant for a given status label (case-insensitive)
 */
export function getVariantForStatus(
  label: string,
  statusMap: Record<string, StatusChipVariant>
): StatusChipVariant {
  const normalizedLabel = label.toLowerCase();
  return statusMap[normalizedLabel] ?? 'default';
}
